<template>
  <div class="is-hidden-mobile page-layout" data-track="Review Analytics view">
    <!-- Header Section Begins -->
    <div class="level page-info-section">
      <div class="level-left page-layout-head">Reviews</div>
      <div class="level-right">
        <tooltip-new :v2="true" iconAlign="middle" class="m-right-15" :isClickAway="true" hyper-link="https://intercom.help/hellopeter/en/articles/4216042-review-analytics-report">&nbsp;&nbsp;About this feature</tooltip-new>
        <date-select-filter
          :filter_items="filters.dateFilter.props"
          :styleProp="filters.dateFilter.styleProp"
          class="custom-dropdown"
          data-track="Review Analytics change date"
          @onDateChange="handleDateChange"
        />
        <export-reviews :bizSlug="businessSlug" :disabled="!isPaidUser" :endDate="filters.dateFilter.endDate"
                        :rating="filters.starRating" :startDate="filters.dateFilter.startDate"/>
      </div>
    </div>
    <!-- Header Section Ends -->
    <hr class="horizontal-row"/>
    <!-- Bar Chart Begins -->
    <div class="columns">
      <div class="column p-y-0">
        <custom-tabs
          :tabs="[{'name':chartReviewsCount+' Reviews', 'components':['ReviewDetailedStats'], 'url':'/analytics/reviews/detailed-stats', 'props':{replyTime: {starRating: filters.replyTime.starRating}, dateFilter:{'startDate': filters.dateFilter.startDate, 'endDate': filters.dateFilter.endDate}}}, {'name':chartAvgStarRating+'  Average star rating', 'components':['AverageRating'], 'url':'/analytics/reviews/average-rating', 'props':{review: {starRating: filters.reviews.starRating, source: filters.reviews.sources}, replyTime: {starRating: filters.replyTime.starRating}, dateFilter:{'startDate': filters.dateFilter.startDate, 'endDate': filters.dateFilter.endDate}}}, {'name':chartAvgReplyTime+'  Average time to reply', 'components':['AverageReplyTime'], 'url':'/analytics/reviews/average-reply-time', 'props':{review: {starRating: filters.reviews.starRating, source: filters.reviews.sources}, dateFilter:{'startDate': filters.dateFilter.startDate, 'endDate': filters.dateFilter.endDate}}}]"
        />
      </div>
      <!-- Average Star Rating Card Begins -->
      <div class="avg-rating-card rating-card-desktop">
        <average-rating-card :starRatings="ratingData"></average-rating-card>
      </div>
      <!-- Average Star Rating Card Ends -->
    </div>
    <!-- Bar Chart Ends -->
    <div class="columns m-l-12 margin-top-3 rating-section">
      <!-- Average Star Rating Card Begins -->
      <div class="avg-rating-card rating-card-tablet">
        <average-rating-card :starRatings="ratingData"></average-rating-card>
      </div>
      <!-- Average Star Rating Card Ends -->
      <!-- Response Rate Part Begins -->
      <response-rate-widget :responseRates="responseRateData"></response-rate-widget>
      <!-- Response Rate Part Ends -->
      <!-- Analytics Card Begins -->
      <div class="columns is-block-tablet-only analytics-card">
        <div class="column is-one-third card-content">
          <span>{{ analyticsData.reviewUsage.creditsUsed || 0 | formatDigit }}</span>
          <p>Review credits used</p>
        </div>
        <div class="column is-one-third card-content">
          <span>{{ analyticsData.reviewUsage.reportedReviews || 0 | formatDigit }}</span>
          <p>Reported reviews</p>
        </div>
        <div class="column is-one-third card-content">
          <span>{{ analyticsData.reviewUsage.deletedReviewsCount || 0 | formatDigit }}</span>
          <p>Deleted reviews</p>
        </div>
      </div>
      <!-- Analytics Card End -->
    </div>
  </div>
</template>

<script>
/* eslint-disable indent,no-new,no-undef,valid-jsdoc,one-var */
import Rating from '@/components/Rating'
import {mapState, mapActions} from 'vuex'
import {ResponseRateWidget} from '@/widgets'
import {AverageRatingCard, DateSelectFilter, CustomTabs, ExportReviews} from '@/components'
import {FETCH_REVIEW_ANALYTICS_USAGE} from '@/store/action.types'
import {misc} from '@/mixins'
import TooltipNew from '../../../widgets/TooltipNew.vue'

export default {
  name: 'ReviewAnalytics',
  metaInfo: {
    title: 'Business Portal | Hellopeter - Review Analytics'
  },
  mixins: [misc],
  components: {
    TooltipNew,
    Rating,
    DateSelectFilter,
    AverageRatingCard,
    ResponseRateWidget,
    CustomTabs,
    ExportReviews
  },
  data () {
    return {
      chartReviewsCount: 0,
      chartAvgStarRating: 0,
      chartAvgReplyTime: 0,
      responseRateData: {
        avgResponseCount: 0,
        totalReviews: 0,
        intervalBreakdown: [
          {
            count: 0,
            interval: '0h - 8h'
          },
          {
            count: 0,
            interval: '8h - 18h'
          },
          {
            count: 0,
            interval: '18h - 36h'
          },
          {
            count: 0,
            interval: '36h - 72h'
          },
          {
            count: 0,
            interval: 'No response'
          }
        ]
      },
      ratingData: [],
      filters: {
        starRating: null,
        dateFilter: {
          label: 'last_6_months',
          endDate: null,
          startDate: null,
          styleProp: {},
          props: [
            {
              name: 'date',
              label: 'Date',
              showApplyBtn: true,
              default: {type: 'months', value: 6, selected: 'Last 6 months'}, // moment.js placeholder in type
              disabled: [],
              elements: [
                'Last 7 days',
                'Previous month',
                'Last 6 months',
                'Last 12 months',
                'Custom'
              ]
            }
          ]
        },
        reviews: {
          starRating: [],
          sources: []
        },
        replyTime: {
          starRating: []
        }
      }
    }
  },
  computed: {
    ...mapState({
      analyticsData: state => state.analytics
    })
  },
  created () {
    this.setUpgradedPackage()

    window.events.$on('starRatingChanged_rds', data => {
      this.filters.starRating = data
      this.filters.reviews.starRating = data
    })

    window.events.$on('sourceChanged_rds', data => {
      this.filters.reviews.sources = data
    })

    window.events.$on('starRatingChanged_art', data => {
      this.filters.starRating = data
      this.filters.replyTime.starRating = data
    })

    if (!('common' in this.analyticsData)) {
      this.analyticsData['common'] = {}
    }

    window.events.$on('dataFetched_ra', data => {
      this.chartReviewsCount = this.analyticsData.common.chartReviewsCount
      this.chartAvgStarRating = this.analyticsData.common.chartAvgStarRating
      this.chartAvgReplyTime = this.analyticsData.common.chartAvgReplyTime
      this.responseRateData = this.analyticsData.common.responseRateData
      this.ratingData = this.analyticsData.common.ratingData
    })
  },
  destroyed () {
    window.events.$off('starRatingChanged_rds')
    window.events.$off('sourceChanged_rds')
    window.events.$off('starRatingChanged_art')
    window.events.$off('dataFetched_ra')
  },
  mounted () {
    this.$gtm.trackEvent({
      event: 'pageLoad',
      virtualPageUrl: this.$router.currentRoute.path,
      virtualPageTitle: 'Review Analytics'
    })
  },
  methods: {
    ...mapActions({
      fetchReviewAnalyticsUsage: `analytics/${FETCH_REVIEW_ANALYTICS_USAGE}`
    }),
    /**
     * If user having to Free/Lite package
     * @return {void}
     */
    setUpgradedPackage () {
      /** If user having Free package, set default filter to previous months */
      if (this.userInfo.subscription.name === 'Free') {
        this.filters.dateFilter.styleProp = 'height: 224px;'
        this.filters.dateFilter.props[0].default.value = 1
        this.filters.dateFilter.props[0].default.selected = 'Previous month'
        this.filters.dateFilter.props[0].disabled = [
          'Last 6 months',
          'Last 12 months',
          'Custom'
        ]
        this.filters.dateFilter.props[0].showApplyBtn = false
      }
    },
    /**
     * Fetching the review analytics stats
     * @return {void}
     */
    fetchAnalyticsStats () {
      const payload = {
        endDate: this.filters.dateFilter.endDate,
        startDate: this.filters.dateFilter.startDate,
        dateInterval: this.customLabel(
          this.filters.dateFilter.startDate,
          this.filters.dateFilter.endDate
        ),
        businessSlug: this.businessSlug
      }
      this.fetchReviewAnalyticsUsage(payload)
    },
    /**
     * Handle Date Dropdown Filter
     * @param {object} dateFilter Date data to sort on
     * @return {void}
     */
    handleDateChange (dateFilter) {
      Object.assign(this.filters.dateFilter, dateFilter)
      this.fetchAnalyticsStats()
    }
  },
  filters: {
    /**
     * Format the integer by separator (comma (,)) after three digits (e.g 1,530)
     * @param val
     * @return {string}
     */
    formatDigit (val) {
      return val.toLocaleString()
    }
  }
}
</script>

<!-- Local CSS Begins -->
<style scoped>
.page-layout {
  margin-left: -16px;
  margin-right: -5px;
}

.page-layout .page-layout-head {
  font-size: 12px;
  margin-left: 20px;
}

.level .level-right a:not(.about-feature):not(.export-btn) {
  width: 130px;
  height: 37px;
  color: #58595b;
  border-radius: 2px;
  border: solid 1px #58595b;
}

.about-feature {
  margin-right: 15px;
}

.analytics-card {
  margin-left: 7px;
}

.analytics-card span {
  font-size: 24px;
  font-weight: bold;
  line-height: 1.38;
  color: #58595b;
}

.analytics-card .card-content {
  width: 120px;
  height: 70px;
  text-align: center;
  border: solid 1px rgba(88, 89, 91, 0.32);
}

.card-content {
  padding: 12px 0;
  margin: 0 7px;
}

.card-content p {
  font-size: 11px;
  line-height: 1.36;
  letter-spacing: normal;
  color: #58595b;
}

.avg-rating-card {
  width: 222px;
  margin-top: 42px;
}

.horizontal-row {
  margin-top: 0;
  margin-bottom: 1rem;
  margin-left: -5%;
  margin-right: -5%;
}

.page-info-section {
  position: relative;
  margin-bottom: 0.7rem;
}

.page-info-section .level-right .padding-bottom-0 {
  width: 302px;
  padding: 0;
  margin-right: 15px;
}

.page-info-section .dropdown-trigger button {
  padding-left: 20px !important;
}

/* Media Queries begins */
/* Media Query For Tablet */
@media screen and (min-width: 767px) {
  .rating-card-tablet {
    display: block;
    margin-bottom: 1%;
  }

  .rating-card-tablet .avg-star-rating {
    margin-left: 0;
  }

  .rating-card-desktop {
    display: none;
  }

  .analytics-card span {
    font-size: 11px;
  }

  .card-content p {
    font-size: 9px;
  }

  .analytics-card .card-content {
    width: auto;
  }
}

/* Media Query For Desktop and Big Screens */
@media screen and (min-width: 1280px) {
  .rating-card-tablet {
    display: none;
  }

  .rating-card-desktop {
    display: block;
  }

  .rating-card-tablet .avg-star-rating {
    margin-left: 1%;
  }

  .analytics-card span {
    font-size: 24px;
  }

  .card-content p {
    font-size: 11px;
  }

  .analytics-card .card-content {
    width: 120px;
  }
}

@media (max-width: 1200px) and (min-width: 768px) {
  .avg-rating-card {
    width: 22%;
  }
}

@media (max-width: 991px) and (min-width: 768px) {
  .avg-rating-card {
    width: 25%;
  }
}

/* Media Queries ends */
.custom-dropdown >>> .button.button-dropdown {
  padding: 0px 50px 0px 20px !important;
}

.custom-dropdown >>> .button.button-dropdown span {
  margin-left: 36px !important;
}
</style>
<!-- Local CSS Ends -->

<!-- Global CSS Begins -->
<style>
div#sideBar .is-padding-top-0 {
  padding-top: 0;
}

.page-info-section .fa-angle-down {
  font-size: 1.5em !important;
}

.page-info-section .fa-angle-down:before {
  right: 1.3em !important;
  border: 3px solid #95979b !important;
  border-right: 0 !important;
  border-top: 0 !important;
}

@media screen and (min-width: 768px) {
  div#sideBar main.column.main-column {
    width: 83%;
  }
}
</style>
<!-- Global CSS Ends -->



// WEBPACK FOOTER //
// src/pages/analytics/reviews/ReviewAnalytics.vue